import { PuzzleContent } from "sockapi/scope-specs/puzzle-content-spec";
import { KeyframeData } from "sockapi/scope-specs/keyframe-spec";
import { ROUND_IDS } from "sockapi/scope-specs/round-spec";
import { CollectibleMetadata } from "sockapi/scope-specs/team-collectibles-spec";
import { AnswerNormalization } from "sockapi/answers";
import { PUZZLES, PIECES } from "sockapi/site-data";

export const UNTITLED_PUZZLE_DISPLAY_NAME = "Piece";

export const getPuzzleDisplayName = (
  puzData: PuzzleMetadata | PuzzleData,
  untitledPuzzleIndex: number | undefined,
  customTitle: string | undefined,
  isAdmin: boolean
): string => {
  const puzReadable =
    "puzReadable" in puzData ? puzData.puzReadable : undefined;
  const adminPrefix = isAdmin ? `[${puzReadable}] ` : "";
  const metaPrefix = (puzData.isMeta ?? false) ? "[META] " : "";
  if (puzData.displayName !== "")
    return `${adminPrefix}${metaPrefix}${puzData.displayName}`;
  if (customTitle !== undefined && customTitle !== "")
    return `${adminPrefix}${metaPrefix}${customTitle}`;
  const untitledPuzzleSuffix =
    untitledPuzzleIndex === undefined ? "" : ` ${untitledPuzzleIndex + 1}`;
  return `${adminPrefix}${metaPrefix}${UNTITLED_PUZZLE_DISPLAY_NAME}${untitledPuzzleSuffix}`;
};

// Guess whether a piece belongs to a puzzle by checking if they have matching
// puzReadables. This doesn't work for site, so use the correspondence map.
export const doesPieceBelongTo = (piece: PuzzleData, puzzle: PuzzleData) => {
  if (piece.puzReadable.startsWith(puzzle.puzReadable)) return true;
  if (piece.roundId !== ROUND_IDS.site) return false;
  if (puzzle.roundId !== ROUND_IDS.site) return false;
  if (puzzle.isMeta) return true;
  const index = PUZZLES.indexOf(puzzle.puzName);
  return PIECES.some(([pc, pz]) => pc === piece.puzName && pz[index] === 1);
};

export const APPROACHABILITY_ICONS = ["🌶️", "🍌", "🟢", "💙"];

export enum PuzzleType {
  STORY = "story",
  PIECE = "piece",
  PUZZLE = "puzzle",
}

// The data about a puzzle that's public.
export type PuzzleMetadata = {
  /** What kind of a thing is this? */
  type: PuzzleType;
  /** The identifier used to refer to the puzzle. */
  puzName: string;
  /** The slug used by Django and shown in the URL bar. */
  slug: string;
  /** Title of the puzzle used everywhere else. */
  displayName: string;
  /**
   * The answer normalization to be used for the puzzle,
   * if different from the uppercase-letters-only default.
   */
  answerNormalization?: AnswerNormalization;
  /** Round slug the puzzle belongs to. */
  roundId?: string;
  /** Width override for the movable, in round units. */
  cols?: number;
  /** Height override for the movable, in round units. */
  rows?: number;
  /** The region this piece belongs to. Only set for pieces. */
  region?: string;
  /** Whether this piece's background is transparent. */
  transparentBackground?: true;
  /** Whether this piece has audio. */
  hasAudio?: true;
  /** Whether this piece should be allowed to rotate. */
  allowRotation: boolean;
  /** Whether this piece should be allowed to flip. */
  allowFlipping?: true;
  /** All images used, if the puzzle is a cutscene. Used for preloading. */
  cutsceneImages?: string[];
  /**
   * Whether this is the final puzzle.
   */
  isBigMeta?: true;
  isMeta?: true;
  isFinalCutscene?: true;
};

export type RoundData = {
  roundId: string;
  displayName: string;
  slug: string;
  order: number;
  boxCols: number;
  boxRows: number;
  pieceCols: number;
  pieceRows: number;
  gridCols: number;
  gridRows: number;
};

export type UnlockingInEdge = {
  unlockGroupId: string;
  /**
   * Satisfy this edge if there are `val` solves in group. Defaults to 1.
   * If -1, that means "never satisfy".
   */
  val?: number;
  /**
   * Satisfy this edge if there are `unlockVal` unlocks in group. Defaults to
   * -1, which means "never satisfy".
   */
  unlockVal?: number;
};
export type UnlockingOutEdge = {
  unlockGroupId: string;
  /** Value added on unlock/solve. Defaults to 1 */
  val?: number;
};

// Data about a public puzzle including private data.
export type PuzzleData = PuzzleMetadata & {
  order: number;

  /** A readable name for the puzzle, strictly internal. */
  puzReadable: string;
  /** Initial x-position for the movable, in *relative* units. */
  initialCol?: number;
  /** Initial y-position for the movable, in *relative* units. */
  initialRow?: number;

  /**
   * Emoji used to represent this puzzle in internal Discord alerts.
   * May be empty if not defined. Currently only used by Django.
   */
  emoji: string;
  /**
   * If set, follows the traditional unlock mechanism where it unlocks
   * once its requirements are satisifed. Otherwise, its unlock must
   * be triggered manually by the server.
   */
  isAutoUnlocked?: true;
  /**
   * Values needed to be reached by unlock groups for the puzzle
   * to be eligible for unlocking.
   */
  unlockInEdges: UnlockingInEdge[];
  /**
   * Values added to the unlock and solve unlock group counters
   * respectively when this puzzle is unlocked or solved.
   */
  unlockOutEdges: UnlockingOutEdge[];
  /**
   * Conditions needed to be met to reveal the solution. If empty, the
   * solution is revealed after solving.
   */
  solutionInEdges?: UnlockingInEdge[];
  /**
   * The puzzle gets easier the more difficulty thresholds are met.
   */
  difficultyThresholds?: UnlockingInEdge[];
  /**
   * The puzzle's answer. If no answer is specified, then an answer
   * cannot be called in and the solve page is inaccessible until
   * the puzzle is solved.
   */
  answer: string | null;

  authors?: string[];
  otherCredits?: string;
};

export type AllPuzzleContent = {
  puzzles: { [puzName: string]: PuzzleContent };
  solutions: { [puzName: string]: PuzzleContent };
};

export type AllPuzzles = {
  rounds: RoundData[];
  puzzles: PuzzleData[];
};

export type PuzzleMessagesData = {
  [puzName: string]: {
    // Message to show for a given answer.
    // If null, use the default "Keep going!" message.
    [answer: string]: string | null;
  };
};

export type CannedHints = { [puzName: string]: string[] };
export type KeyframesData = {
  firstKeyframeIds: { [puzName: string]: string };
  keyframes: { [keyframeId: string]: KeyframeData };
};

type CollectibleData = CollectibleMetadata & {
  maxNumPerTeam?: number;
};
export type CollectiblesData = {
  [collectibleId: string]: CollectibleData;
};

export type PuzzlesDBData = {
  allPuzzles: AllPuzzles;
  allPuzzleContent: AllPuzzleContent;
  puzzleMessages: PuzzleMessagesData;
  cannedHints: CannedHints;
  keyframesData: KeyframesData;
  collectiblesData: CollectiblesData;
};

/**
 * PuzzlesDB data should be readonly, so store custom puzzles separately
 * from the puzzles DB.
 */
export type CustomPuzzles = {
  [puzName: string]: PuzzleData;
};
